import { AHButton, AHIcon, AHText, Box, useTheme } from "arkh";
import React, { useCallback, useLayoutEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { graphql, useLazyLoadQuery } from "react-relay";
import { HeaderButton } from "../../../components";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { useModalInsets } from "../../../components/mobile/useModalInsets";

import { UserLoggedInNavigatorScreenProps } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { KPISubTeamNameInput } from "../components/KPISubTeamNameInput";
import { KPIEditSubTeamQuery } from "./__generated__/KPIEditSubTeamQuery.graphql";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { Platform } from "react-native";
import { TeamSelectList } from "widgets/src/TeamSelectList";

export const KPIEditSubTeam = withWebModalStack(KPIEditSubTeamC);
function KPIEditSubTeamC(
  props: UserLoggedInNavigatorScreenProps<"KPIEditSubTeam">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerKPIEditSubTeam {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerKPIEditSubTeam({
  route: { params },
  navigation,
  fetchKey,
}: UserLoggedInNavigatorScreenProps<"KPIEditSubTeam"> & {
  fetchKey: string;
}) {
  const data = useLazyLoadQuery<KPIEditSubTeamQuery>(
    graphql`
      query KPIEditSubTeamQuery {
        currentStore @required(action: THROW) {
          publicTeams {
            ...TeamSelectList_Team
          }
        }
      }
    `,
    {},
    { fetchKey }
  );

  const [teamId, setTeamId] = useState(params.section.assignedTeam.id);
  const [title, setTitle] = useState(params.section.title ?? "");
  const insets = useModalInsets();

  const onDelete = useCallback(() => {
    navigation.navigateDeprecated("KPIEdit", {
      screen: "KPIEditSections",
      params: {
        ...params,
        removeSections: [...params.removeSections, params.section.id],
        localSections: params.localSections.filter(
          (ls) => ls.id !== params.section.id
        ),
      },
    });
  }, [navigation, params]);

  const onSave = useCallback(() => {
    const removeSections = [
      ...params.removeSections,
      ...params.remoteSections.flatMap((rs) =>
        rs.title === null && teamId === rs.assignedTeam.id ? [rs.id] : []
      ),
    ];
    const ls = params.localSections.findIndex(
      ({ id }) => id === params.section.id
    );

    const localSections =
      ls === -1
        ? [
            ...params.localSections,
            {
              ...params.section,
              title,
              assignedTeam: {
                id: teamId,
              },
            },
          ]
        : params.localSections.map((ls) =>
            ls.id === params.section.id
              ? {
                  ...ls,
                  assignedTeam: { id: teamId },
                  title,
                }
              : ls
          );
    navigation.navigateDeprecated("KPIEdit", {
      screen: "KPIEditSections",
      params: {
        ...params,
        localSections,
        removeSections,
      },
    });
  }, [navigation, params, teamId, title]);

  const intl = useIntl();
  const theme = useTheme();
  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => <HeaderButton type="SAVE" onPress={onSave} />,
      headerShown: true,
      headerStyle: {
        backgroundColor: theme.surface,
      },
      headerLeft:
        Platform.OS === "ios"
          ? () => <HeaderButton type="CANCEL" onPress={navigation.goBack} />
          : undefined,
      title: intl.formatMessage({
        defaultMessage: "Edit measurement area",
        description: "EditSubTeamModal header title",
      }),
    });
  }, [params, navigation, title, teamId, onSave, theme.surface, intl]);

  return (
    <TeamSelectList
      ListHeaderComponent={
        <KPISubTeamNameInput name={title} setName={setTitle} />
      }
      ListFooterComponent={
        <Box spaceTop="large">
          <AHButton themeType="negative" onPress={onDelete}>
            <AHIcon type="trash" color="lightText" />
            <AHText
              type="title-small"
              color="lightText"
              spaceHorizontal="small"
            >
              <FormattedMessage
                defaultMessage="Remove measurement area"
                description="EditSubTeamModal remove measurement area"
              />
            </AHText>
          </AHButton>
        </Box>
      }
      teams={data.currentStore.publicTeams}
      insets={insets}
      onItemPress={(itemId) => {
        setTeamId(itemId);
      }}
      selected={[teamId]}
      contentBox={{
        space: "medium",
        spaceTop: "medium",
      }}
    />
  );
}
